import { Button, EmptyState, NoTasksIcon } from '@bp/ui-components';
import styles from '../Header/components/_Flyouts.module.scss';
import dayjs, { Dayjs } from 'dayjs';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { useState } from 'react';
import { useAuthClaims } from '../../hooks/useAuthClaims';
import { EventItem } from '../EventItem/EventItem';
import { copyEventLinkToClipboard, endEvent, startAndJoinEvent, startEvent } from 'utils/eventHelper';
import { FragmentType, getFragmentData, graphql } from 'client/gql';
import { eventColors } from 'utils/dateCalculations';
import { usePermissionChecker } from 'hooks/usePermissionChecker';
import { GroupRoles } from '@bp/pim-auth-constants';

const ConvertEventFieldsDocument = graphql(`
  fragment ConvertEventFields on CalendarEvent {
    uuid
    __typename
    categories
    holderConnection {
      edges {
        node {
          ... on Group {
            uuid
            displayName
            organization {
              uuid
            }
          }
          ... on Profile {
            uuid
            displayName
            organization {
              uuid
            }
          }
          ... on Organization {
            uuid
            displayName
          }
        }
      }
    }
    classShortNames
    description
    duration
    keywords
    locations {
      uuid
      __typename
    }
    start
    title
    virtualLocations {
      uuid
      __typename
    }
  }
`);

const convertEvent = (fragment?: FragmentType<typeof ConvertEventFieldsDocument> | undefined) => {
  const event = getFragmentData(ConvertEventFieldsDocument, fragment);
  if (!event) return undefined;

  const eventColor = eventColors(event.categories ?? []);

  const holderNode = event.holderConnection.edges.find((h) => (h.node.uuid ?? '').length > 0)?.node;

  return {
    categories: event.categories ?? [],
    classShortNames: event.classShortNames,
    color: eventColor,
    description: event.description ?? '',
    duration: event.duration ?? undefined,
    keywords: event.keywords ?? [],
    locationsCount: event.locations.length,
    originDisplayName: holderNode?.displayName ?? '',
    start: dayjs(event.start),
    title: event.title ?? '',
    type: 'event',
    uuid: event.uuid,
    virtualLocationsCount: event.virtualLocations.length,
    holderUuid: holderNode?.uuid ?? 'no-holder',
    organizationUuid: holderNode && 'organization' in holderNode ? holderNode.organization.uuid : 'no-organization',
  };
};

const MeetingsFlyoutFieldsDocument = graphql(`
  fragment MeetingsFlyoutFields on Query {
    runningMeetings: calendarEvents(
      options: { sort: [{ start: ASC }], limit: 500 }
      where: {
        AND: [
          {
            OR: [
              { virtualLocations_SOME: { running: true } }
              { daysConnection_SOME: { node: { date: $dayConnection } } }
            ]
          }
          {
            OR: [
              { holderConnection: { Organization: { node: { uuid: $organizationUuid } } } }
              { attendees_SOME: { uuid: $profile } }
              { owner: { uuid: $profile } }
            ]
          }
          { virtualLocations_SOME: { running: true } }
        ]
      }
    ) {
      uuid
      __typename
      guestsAllowed
      owner {
        uuid
        __typename
      }
      organization {
        uuid
        __typename
      }
      virtualLocations {
        uuid
        __typename
        token
      }
      ...ConvertEventFields
    }
    notRunningMeetings: calendarEvents(
      options: { sort: [{ start: ASC }], limit: 500 }
      where: {
        AND: [
          {
            OR: [
              { virtualLocations_SOME: { running: true } }
              { daysConnection_SOME: { node: { date: $dayConnection } } }
            ]
          }
          {
            OR: [
              { holderConnection: { Organization: { node: { uuid: $organizationUuid } } } }
              { attendees_SOME: { uuid: $profile } }
              { owner: { uuid: $profile } }
            ]
          }
          { virtualLocations_SOME: { running: false } }
        ]
      }
    ) {
      uuid
      __typename
      guestsAllowed
      start
      duration
      owner {
        uuid
        __typename
      }
      organization {
        uuid
        __typename
      }
      virtualLocations {
        uuid
        __typename
        token
      }
      ...ConvertEventFields
    }
    ...PermissionCheckerFields
  }
`);

const MeetingsFlyout = ({ query }: { query: FragmentType<typeof MeetingsFlyoutFieldsDocument> | undefined }) => {
  const { t } = useTranslation();
  const { pimAuthClaims } = useAuthClaims();

  const [isLoading, setLoading] = useState<boolean>(false);

  const fragment = getFragmentData(MeetingsFlyoutFieldsDocument, query);
  const runningMeetings = fragment?.runningMeetings;
  const plannedMeetings = fragment?.notRunningMeetings.filter(
    (e) => dayjs().add(1, 'days').isAfter(e.start) && dayjs().subtract(Number(e.duration), 's').isBefore(e.start),
  );

  const permissions = usePermissionChecker(fragment);

  const hasRunningMeetings = runningMeetings && runningMeetings.length > 0;
  const hasPlannedMeetings = plannedMeetings && plannedMeetings.length > 0;

  const startMeeting = async (virtualLocationsToken?: string) => {
    setLoading(true);
    if (virtualLocationsToken) {
      await startEvent(virtualLocationsToken);
    }

    setLoading(false);
  };

  const copyToClipBoard = async (virtualLocationsToken?: string) => {
    if (virtualLocationsToken) {
      const uri = import.meta.env.VITE_APP_REDIRECT_URI + '/join/' + virtualLocationsToken;
      await copyEventLinkToClipboard(uri);
    }
  };

  const startAndJoinMeeting = async ({
    virtualLocationsToken,
  }: {
    virtualLocationsToken?: string;
  }) => {
    setLoading(true);
    if (virtualLocationsToken) {
      await startAndJoinEvent(
        virtualLocationsToken,
        pimAuthClaims.getProfile(),
      );
    }
    setLoading(false);
  };

  const endMeeting = async (virtualLocationsToken?: string) => {
    setLoading(true);
    if (virtualLocationsToken) {
      await endEvent(virtualLocationsToken);
    }
    setLoading(false);
  };

  const meetingStartable = (start: Dayjs): boolean => {
    return dayjs(start).isBefore(dayjs().add(1, 'hour'));
  };

  return (
    <div className={classNames(styles['flyout-body'], styles.meetings, 'has-scrollbar')}>
      {!hasRunningMeetings && !hasPlannedMeetings ? (
        <EmptyState forcedHeight='500px' maxWidth='200px' title={t('meetings.noMeeting')} icon={<NoTasksIcon />} />
      ) : (
        <>
          <div className={styles.running}>
            <div className={styles.header}>{t('meetings.title')}</div>
            {!hasRunningMeetings ? (
              <EmptyState size='small' title={t('meetings.noRunningMeetings')} hideIcon padding='l' />
            ) : (
              runningMeetings.map((m) => {
                const event = convertEvent(m);
                return (
                  <div className={styles['flyout-item']} key={m.uuid}>
                    {event && <EventItem event={event} onClick={() => {}} isEmbedded isCompact={true} />}
                    <div className={styles.actions}>
                      {permissions?.canEndMeetings({ organization: m.organization, ownerUuid: m.owner.uuid }) && (
                        <Button
                          isLoading={isLoading}
                          hierarchy='secondary'
                          onClick={() => endMeeting(m.virtualLocations[0]?.token)}
                        >
                          {t('meetings.end')}
                        </Button>
                      )}
                      <Button
                        isLoading={isLoading}
                        hierarchy='primary'
                        onClick={() =>
                          startAndJoinMeeting({
                            virtualLocationsToken: m.virtualLocations[0]?.token,
                          })
                        }
                      >
                        {t('meetings.join')}
                      </Button>
                      {m.guestsAllowed && (
                        <Button
                          isLoading={isLoading}
                          hierarchy='tertiary'
                          onClick={() => copyToClipBoard(m.virtualLocations[0]?.token)}
                        >
                          {t('meetings.copyLink')}
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>

          <div className={styles.planned}>
            <div className={styles.header}>{t('meetings.plannedMeetings')}</div>
            {!hasPlannedMeetings ? (
              <EmptyState size='small' title={t('meetings.noPlannedMeetings')} hideIcon padding='l' />
            ) : (
              <>
                {plannedMeetings?.map((m) => {
                  const event = convertEvent(m);
                  const isEditor = permissions?.hasGroupRole(
                    GroupRoles.Editor, {
                    uuid: event?.holderUuid ?? 'no-holder',
                    organization: { uuid: event?.organizationUuid ?? 'no-organization' },
                  });

                  return (
                    <div className={styles['flyout-item']} key={m.uuid}>
                      {event && (
                        <EventItem
                          className={styles.event}
                          event={event}
                          isCompact={true}
                          onClick={() => {}}
                          isEmbedded
                        />
                      )}
                      {isEditor && meetingStartable(dayjs(m.start)) && (
                        <div className={styles.actions}>
                          <Button
                            isLoading={isLoading}
                            type='submit'
                            hierarchy='secondary'
                            className='mb-2'
                            onClick={async () =>
                              await startAndJoinMeeting({
                                virtualLocationsToken: m.virtualLocations[0]?.token,
                              })
                            }
                          >
                            {t('meetings.startAndJoin')}
                          </Button>
                          <Button
                            isLoading={isLoading}
                            hierarchy='primary'
                            onClick={() => startMeeting(m.virtualLocations[0]?.token)}
                          >
                            {t('meetings.start')}
                          </Button>
                          {m.guestsAllowed && (
                            <Button
                              isLoading={isLoading}
                              hierarchy='tertiary'
                              onClick={() => copyToClipBoard(m.virtualLocations[0]?.token)}
                            >
                              {t('meetings.copyLink')}
                            </Button>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export { MeetingsFlyout };
